/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * DateTextField
 *
 * @file DateTextField.jsx
 * @author Satyaki Bose
 */



import React from 'react';
import { shift, offset } from '@floating-ui/react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Typography } from '@material-ui/core';
import Utils from '../../common/utils';
import { parseDate, formatDateToStorage, formatMonthYear } from '../../common/dateTimeUtils';
import AetDateFieldStyle from './Styles/AetDateFieldStyle';
import Icon from '../icons/Icon';

const DateTextField = ({ label, value, onChange, parentApp, icons, hasError }) => {
  const classes = Utils.getSyllabusStyles(
    parentApp,
    { ereader: null, aet: AetDateFieldStyle },
    () => ({})
  );

  const popperContainer = React.useCallback(
    ({ children }) => (
      <div className={classes.datePickerPortalWrapper}>{children}</div>
    ),
    [classes.datePickerPortalWrapper]
  );

  if (!label) return null;

  const handleDateChange = (date) => {
    const formattedDate = formatDateToStorage(date);
    onChange({ target: { value: formattedDate } });
  };

  const selectedDate = parseDate(value);

  const renderCustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    decreaseYear,
    increaseYear,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
    prevYearButtonDisabled,
    nextYearButtonDisabled
  }) => (
    <div className={classes.customHeader}>
      <button
        type="button"
        onClick={decreaseYear}
        className={classes.navButton}
        aria-label="Previous year"
        disabled={prevYearButtonDisabled}
      >
        <Icon name="CaretDoublePrevIcon" />
      </button>
      <button
        type="button"
        onClick={decreaseMonth}
        className={classes.navButton}
        aria-label="Previous month"
        disabled={prevMonthButtonDisabled}
      >
        <Icon name="CaretPrevIcon" />
      </button>
      <span className={classes.monthYearLabel}>
        {formatMonthYear(date)}
      </span>
      <button
        type="button"
        onClick={increaseMonth}
        className={classes.navButton}
        aria-label="Next month"
        disabled={nextMonthButtonDisabled}
      >
        <Icon name="CaretNextIcon" />
      </button>
      <button
        type="button"
        onClick={increaseYear}
        className={classes.navButton}
        aria-label="Next year"
        disabled={nextYearButtonDisabled}
      >
        <Icon name="CaretDoubleNextIcon" />
      </button>
    </div>
  );

  return (
    <div className={classes.dateWrapper}>
      <Typography className={classes.label}>{label}</Typography>
      <div className={classes.datePickerContainer}>
        <DatePicker
          selected={selectedDate}
          onChange={handleDateChange}
          dateFormat="MM/dd/yy"
          placeholderText="DD/MM/YY"
          className={`${classes.dateInput} ${hasError ? classes.dateInputError : ''}`}
          showPopperArrow={false}
          popperPlacement="bottom-start"
          popperProps={{
            middleware: [
              offset(8),
              shift({
                padding: 10,
                boundary: document.body,
                crossAxis: true
              })
            ]
          }}
          popperClassName={classes.datePickerPopper}
          portalId="react-datepicker-portal"
          popperContainer={popperContainer}
          calendarStartDay={0}
          renderCustomHeader={renderCustomHeader}
          minDate={new Date()}
        />
        {icons?.calendar && (
          <button
            onClick={() => {}}
            className={classes.calendarIcon}
            type="button"
            aria-label="Calendar icon"
          >
            {icons.calendar}
          </button>
        )}
      </div>
    </div>
  );
};

DateTextField.propTypes = {
  label: PropTypes.string,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  parentApp: PropTypes.string.isRequired,
  icons: PropTypes.object,
  hasError: PropTypes.bool
};

DateTextField.defaultProps = {
  label: '',
  icons: null,
  hasError: false
};

export default DateTextField;
